import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import { communications, documents, parties, requests } from '../../api/endpoints';
import type {
    Contact,
    Document,
    DocumentApproval,
    DocumentCheck,
    DocumentCheckDiscrepancy,
    DocumentCheckResolution,
    DocumentTemplate,
    DocumentVersion,
    FileBlob,
    GenerationJob,
    IssuedDocument,
    Message,
    MessageRequest,
    MissingTemplateField,
    RegistryCheck,
    RequestContact,
    RequestSnapshot,
    VersionCompareResult
} from '../../api/types';
import {
    Card,
    Column,
    DataTable,
    Empty,
    ErrorBox,
    Field,
    fmtDate,
    KV,
    label,
    shortId,
} from '../../components/ui';
import { useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';

// Локальные словари статусов контура документов (contract v8 Stage 4).
const CHECK_LABELS: Record<string, string> = {
  not_checked: 'Не проверено', match: 'Совпадает', mismatch: 'Есть расхождения',
  accepted_mismatch: 'Расхождения приняты', blocked: 'Проверка невозможна',
};
const CHECK_TONES: Record<string, string> = {
  match: 'ok', mismatch: 'bad', accepted_mismatch: 'warn', blocked: 'warn', not_checked: 'muted',
};
// Причины расхождений проверки (Fix2, F2-04), blocked-строк эталона (§10.3)
// и прочие machine-readable reason. Неизвестные показываются как есть (fallback).
const REASON_LABELS: Record<string, string> = {
  ambiguous_binding: 'неоднозначная привязка к продукту',
  product_not_found: 'продукт не найден в документе',
  value_mismatch: 'значение не совпадает',
  missing_reference: 'нет значения в эталоне',
  reference_unavailable: 'нет эталона для проверки — требуется ручная проверка',
  empty_reference: 'эталон пуст — проверять нечего',
  text_unavailable: 'текст документа не извлекается — требуется ручная проверка',
  historical_profile_unavailable:
    'исторический профиль проверки недоступен — требуется ручная проверка',
  missing: 'не заполнено',
  // === WT3 docs ===: причины посполевых строк final-check (§9).
  extra_in_document: 'лишнее значение в документе — в эталоне отсутствует',
  unconfirmed: 'поле извлечено, но не подтверждено',
  no_confirmed_fields: 'нет подтверждённых полей финального документа',
  template_unapproved: 'образец шаблона не согласован',
  unknown_field: 'неизвестное поле шаблона',
  no_item: 'позиция снимка не определена',
  ambiguous: 'позиция снимка неоднозначна',
};
// === WT3 docs ===: статусы полевого сравнения final-check (§9) и
// проверки реестра.
const FIELD_STATUS_LABELS: Record<string, string> = {
  match: 'Совпадает', mismatch: 'Отличается', not_found: 'Не найдено',
  unverifiable: 'Невозможно однозначно проверить',
  unconfirmed: 'Не подтверждено', no_reference: 'Нет эталона',
};
const FIELD_STATUS_TONES: Record<string, string> = {
  match: 'ok', mismatch: 'bad', not_found: 'bad',
  unverifiable: 'warn', unconfirmed: 'muted', no_reference: 'muted',
};
const REGISTRY_LABELS: Record<string, string> = {
  found: 'Найден в реестре', not_found: 'Не найден в реестре',
  mismatch: 'Данные расходятся', unavailable: 'Реестр недоступен',
};
const REGISTRY_TONES: Record<string, string> = {
  found: 'ok', not_found: 'warn', mismatch: 'bad', unavailable: 'muted',
};
// Вид проверки версии (DocumentCheck.kind, WT3 §9).
const CHECK_KIND_LABELS: Record<string, string> = {
  reference: 'против эталона', final_fields: 'поля финального документа',
};
const APPROVAL_LABELS: Record<string, string> = {
  pending: 'На согласовании', approved: 'Согласовано', rejected: 'Отклонено',
};
const APPROVAL_TONES: Record<string, string> = { pending: 'info', approved: 'ok', rejected: 'bad' };
const JOB_LABELS: Record<string, string> = {
  pending: 'В очереди', running: 'Выполняется', done: 'Завершено', error: 'Ошибка',
  // === WT3 docs ===: терминальный статус — образец шаблона не согласован (§10).
  template_unapproved: 'Образец не согласован',
};
const JOB_TONES: Record<string, string> = {
  pending: 'muted', running: 'info', done: 'ok', error: 'bad',
  template_unapproved: 'warn',
};
const ORIGIN_LABELS: Record<string, string> = {
  initial: 'исходная', generated: 'сгенерирована', received: 'получена',
};
const ORIGIN_TONES: Record<string, string> = { initial: 'muted', generated: 'info', received: 'ok' };
const SCAN_LABELS: Record<string, string> = {
  pending: 'На сканировании', scanning: 'Сканируется', clean: 'Чистый',
  infected: 'Инфицирован', quarantined: 'Карантин', error: 'Ошибка сканирования',
};
const SCAN_TONES: Record<string, string> = {
  clean: 'ok', infected: 'bad', quarantined: 'bad', error: 'warn', pending: 'muted', scanning: 'info',
};

const ISSUED_DOC_TYPES = ['ss', 'ds', 'refusal_letter', 'aip', 'sgr', 'other', 'unknown'];

// Отображение страны в варианте шаблона (ISO-код → короткая русская подпись).
const TEMPLATE_COUNTRY_LABELS: Record<string, string> = {
  RB: 'РБ', BY: 'РБ', RU: 'РФ', KZ: 'КЗ', AM: 'АМ', KG: 'КГ',
};
// Токены field_path эталона/missing_fields → русские подписи (фолбэк, когда
// backend не прислал label для недостающего поля шаблона).
const FIELD_TOKEN_LABELS: Record<string, string> = {
  applicant: 'заявитель', manufacturer: 'изготовитель', payer: 'плательщик',
  supplier: 'поставщик', request: 'заявка', parties: '', items: 'позиция',
  products: 'продукция', name: 'название', number: 'номер', title: 'название',
  legal_address: 'юридический адрес', address: 'адрес', director: 'директор',
  director_name: 'директор', name_rp: 'директор (Р.П.)', email: 'email',
  phone: 'телефон', position: 'должность', inn: 'ИНН', tax_id: 'ИНН',
  ogrn: 'ОГРН', registration_id: 'рег. номер', tnved: 'ТН ВЭД',
  model: 'модель', article: 'артикул', product_name: 'продукция',
  document_type: 'тип документа', country_of_entry: 'страна ввоза',
  production_mode: 'режим выпуска (партия/серийное производство)',
};

/** Подпись шаблона генерации с вариантом: «СС · РБ · ТР ТС 010 · v1». */
function templateLabel(t: DocumentTemplate): string {
  const parts: string[] = [label(t.type)];
  if (t.country) parts.push(TEMPLATE_COUNTRY_LABELS[t.country] ?? t.country);
  if (t.regulation) {
    parts.push(/^\d/.test(t.regulation) ? `ТР ТС ${t.regulation}` : t.regulation);
  }
  parts.push(`v${t.version ?? '?'}`);
  if (t.active === false) parts.push('(неактивен)');
  // === WT3 docs ===: несогласованный образец помечаем явно — генерация
  // по нему завершится статусом template_unapproved.
  if (t.approved === false) parts.push('(образец не согласован)');
  return parts.join(' · ');
}

/** missing_fields из 400 generation-job: [{field, label?, reason?}] либо
    список field_path-строк — достаём из ApiError.fieldErrors (сюда попадает
    field_errors ответа либо всё тело без code/detail/message). */
function missingFieldsOf(err: unknown): MissingTemplateField[] {
  const fe = err instanceof ApiError ? err.fieldErrors : null;
  const raw = Array.isArray(fe)
    ? fe
    : (fe && typeof fe === 'object'
        ? (fe as Record<string, unknown>).missing_fields
          ?? (fe as Record<string, unknown>).missing
        : undefined);
  if (!Array.isArray(raw)) return [];
  const out: MissingTemplateField[] = [];
  for (const m of raw) {
    if (typeof m === 'string') { out.push({ field: m }); continue; }
    if (m && typeof m === 'object') {
      const rec = m as Record<string, unknown>;
      const field = String(rec.field ?? rec.field_path ?? rec.path ?? '');
      const lbl = typeof rec.label === 'string' && rec.label.trim() ? rec.label : undefined;
      const reason = typeof rec.reason === 'string' && rec.reason ? rec.reason : undefined;
      if (field || lbl) out.push({ field, label: lbl, reason });
    }
  }
  return out;
}

/** Человекочитаемая подпись field_path, когда backend не прислал label:
    items.0.parties.applicant.legal_address → «позиция 1 · заявитель · юридический адрес»;
    items.0.products.1.model → «позиция 1 · продукт 2 · модель» (F2-04).
    productName — необязательный resolver имени продукта по индексам
    (items.<i>.products.<j>) из снимка: подпись «продукт 2 «Насос…»». */
function humanFieldPath(
  path: string,
  productName?: (itemIdx: number, prodIdx: number) => string,
): string {
  if (!path) return '—';
  const tokens = path.split('.').filter(Boolean);
  const parts: string[] = [];
  let itemIdx = -1;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const next = tokens[i + 1];
    if (t === 'items' && next !== undefined && /^\d+$/.test(next)) {
      itemIdx = Number(next);
      parts.push(`позиция ${itemIdx + 1}`);
      i += 1;
      continue;
    }
    if (t === 'products' && next !== undefined && /^\d+$/.test(next)) {
      const prodIdx = Number(next);
      const pname = productName?.(itemIdx, prodIdx) ?? '';
      parts.push(`продукт ${prodIdx + 1}${pname ? ` «${pname}»` : ''}`);
      i += 1;
      continue;
    }
    if (t === 'items' || t === 'products' || t === 'parties') continue;
    parts.push(FIELD_TOKEN_LABELS[t] ?? t);
  }
  return parts.join(' · ') || path;
}

/** Первая буква заглавная — для самостоятельных подписей в ячейках таблиц. */
function capFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function Badge({ value, labels, tones }: { value?: string | null; labels: Record<string, string>; tones: Record<string, string> }) {
  if (!value) return <span className="badge muted">—</span>;
  return <span className={`badge ${tones[value] ?? 'muted'}`}>{labels[value] ?? label(value)}</span>;
}

/** discrepancies: массив [{field_path, expected, reason?, role?, observed?}]
    (Fix2) либо {fields: [...], resolution: {...}} после resolve-check (§10.4);
    на всякий случай принимаем и плоский dict {path: expected}. */
function checkDetails(d: DocumentCheck['discrepancies']): {
  list: DocumentCheckDiscrepancy[];
  resolution: DocumentCheckResolution['resolution'] | null;
} {
  if (!d) return { list: [], resolution: null };
  if (Array.isArray(d)) return { list: d, resolution: null };
  const rec = d as DocumentCheckResolution & Record<string, unknown>;
  const list = Array.isArray(rec.fields)
    ? rec.fields
    : Object.entries(rec)
        .filter(([k]) => k !== 'resolution')
        .map(([k, v]) => ({ field_path: k, expected: v }));
  return { list, resolution: rec.resolution ?? null };
}

function fmtExpected(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function DocumentsTab({ data }: { data: CardData }) {
  const requestId = data.request.id;

  const docs = useAsync<Document[]>(
    () => documents.documents({ request: requestId }).catch(() => [] as Document[]),
    [requestId],
  );
  const snapshots = useAsync(
    () => requests.snapshots(requestId).catch(() => []),
    [requestId],
  );
  const templates = useAsync<DocumentTemplate[]>(
    () => documents.templates().catch(() => [] as DocumentTemplate[]),
    [],
  );
  const reqContacts = useAsync<RequestContact[]>(
    () => requests.contacts(requestId).catch(() => [] as RequestContact[]),
    [requestId],
  );
  const contacts = useAsync<Contact[]>(
    () => parties.contacts().catch(() => [] as Contact[]),
    [],
  );
  const msgLinks = useAsync<MessageRequest[]>(
    () => communications.messageLinks({ request: requestId }).catch(() => [] as MessageRequest[]),
    [requestId],
  );
  const msgs = useAsync<Message[]>(
    () => communications.messages({ request: requestId }).catch(() => [] as Message[]),
    [requestId],
  );
  const blobs = useAsync<FileBlob[]>(
    () => documents.fileBlobs({ request: requestId }).catch(() => [] as FileBlob[]),
    [requestId],
  );
  const jobs = useAsync<GenerationJob[]>(
    () => documents.generationJobs().catch(() => [] as GenerationJob[]),
    [requestId],
  );
  // Проверки/согласования/выданные документы — scoped-списки одним запросом,
  // на клиенте фильтруем по версиям/позициям этой заявки.
  const allChecks = useAsync<DocumentCheck[]>(
    () => documents.documentChecks().catch(() => [] as DocumentCheck[]),
    [requestId],
  );
  const allApprovals = useAsync<DocumentApproval[]>(
    () => documents.documentApprovals().catch(() => [] as DocumentApproval[]),
    [requestId],
  );
  const allIssued = useAsync<IssuedDocument[]>(
    () => documents.issuedDocuments().catch(() => [] as IssuedDocument[]),
    [requestId],
  );

  // Версии всех документов заявки (по одному запросу на документ).
  const versions = useAsync<DocumentVersion[]>(async () => {
    const list = docs.data ?? [];
    if (!list.length) return [];
    const parts = await Promise.all(
      list.map((d) => documents.documentVersions({ document: d.id }).catch(() => [] as DocumentVersion[])),
    );
    return parts.flat();
  }, [docs.data]);

  const docMap = useMemo(() => new Map((docs.data ?? []).map((d) => [d.id, d])), [docs.data]);
  const templateMap = useMemo(() => new Map((templates.data ?? []).map((t) => [t.id, t])), [templates.data]);
  const itemMap = useMemo(() => new Map(data.items.map((i) => [i.id, i])), [data.items]);
  const contactMap = useMemo(() => new Map((contacts.data ?? []).map((c) => [String(c.id), c])), [contacts.data]);
  const versionMap = useMemo(() => new Map((versions.data ?? []).map((v) => [v.id, v])), [versions.data]);
  const blobMap = useMemo(() => new Map((blobs.data ?? []).map((b) => [b.id, b])), [blobs.data]);
  const versionIds = useMemo(() => new Set((versions.data ?? []).map((v) => v.id)), [versions.data]);
  const itemIds = useMemo(() => new Set(data.items.map((i) => i.id)), [data.items]);
  const snapshotIds = useMemo(() => new Set((snapshots.data ?? []).map((s) => s.id)), [snapshots.data]);

  /** Снимки заявки, новые сверху. */
  const snapshotList = useMemo(
    () => [...(snapshots.data ?? [])].sort((a, b) => (b.version ?? 0) - (a.version ?? 0)),
    [snapshots.data],
  );

  /** Контакты заявки для согласования; если не привязаны — весь справочник. */
  const contactOptions = useMemo<Contact[]>(() => {
    const linked = new Set((reqContacts.data ?? []).map((rc) => String(rc.contact)));
    const all = contacts.data ?? [];
    const scoped = all.filter((c) => linked.has(String(c.id)));
    return scoped.length ? scoped : all;
  }, [reqContacts.data, contacts.data]);

  /** Сообщения, привязанные к заявке (для выбора evidence). */
  const requestMessages = useMemo(() => {
    const all = msgs.data ?? [];
    const linked = new Set((msgLinks.data ?? []).map((l) => String(l.message)));
    return linked.size > 0 ? all.filter((m) => linked.has(m.id)) : all;
  }, [msgs.data, msgLinks.data]);

  /** Проверки, сгруппированные по версии: индекс 0 — последняя (backend сортирует -created_at). */
  const checksByVersion = useMemo(() => {
    const m = new Map<string, DocumentCheck[]>();
    for (const c of allChecks.data ?? []) {
      if (!c.version || !versionIds.has(c.version)) continue;
      const arr = m.get(c.version) ?? [];
      arr.push(c);
      m.set(c.version, arr);
    }
    return m;
  }, [allChecks.data, versionIds]);

  const approvalsByVersion = useMemo(() => {
    const m = new Map<string, DocumentApproval[]>();
    for (const a of allApprovals.data ?? []) {
      if (!a.version || !versionIds.has(a.version)) continue;
      const arr = m.get(a.version) ?? [];
      arr.push(a);
      m.set(a.version, arr);
    }
    return m;
  }, [allApprovals.data, versionIds]);

  const issuedByVersion = useMemo(() => {
    const m = new Map<string, IssuedDocument[]>();
    for (const i of allIssued.data ?? []) {
      const inScope = (i.version && versionIds.has(i.version)) || (i.request_item && itemIds.has(i.request_item));
      if (!inScope || !i.version) continue;
      const arr = m.get(i.version) ?? [];
      arr.push(i);
      m.set(i.version, arr);
    }
    return m;
  }, [allIssued.data, versionIds, itemIds]);

  /** Jobs генерации, относящиеся к снимкам этой заявки. */
  const requestJobs = useMemo(
    () => (jobs.data ?? []).filter((j) => j.snapshot && snapshotIds.has(j.snapshot)),
    [jobs.data, snapshotIds],
  );

  const docLabel = (d?: Document) =>
    d ? `${label(d.type)}${d.request_item ? ` · ${itemMap.get(d.request_item)?.title ?? shortId(d.request_item)}` : ''}` : '—';
  const versionLabel = (v?: DocumentVersion | null) =>
    v ? `v${v.version ?? '?'} · ${docLabel(docMap.get(v.document ?? ''))}` : '—';
  const contactLabel = (id?: string | null, fallback?: string) =>
    (id ? contactMap.get(String(id))?.name : undefined) ?? fallback ?? (id ? shortId(id) : '—');

  const multiItem = data.items.length > 1;

  /** Позиции снимка по structured_data.items (снимок позиции — один item, §10.3). */
  const snapshotItemIds = (s: RequestSnapshot): string[] => {
    const items = (s.structured_data as { items?: unknown } | undefined)?.items;
    if (!Array.isArray(items)) return [];
    return items
      .map((i) => (i as { id?: unknown })?.id)
      .filter((x): x is string => typeof x === 'string' && x.length > 0);
  };
  const snapshotScopeLabel = (s: RequestSnapshot): string => {
    const ids = snapshotItemIds(s);
    if (ids.length === 1) return itemMap.get(ids[0])?.title ?? shortId(ids[0]);
    return ids.length > 1 ? 'Вся заявка' : '—';
  };

  // ---------- Состояние команд ----------

  const [selDocId, setSelDocId] = useState('');
  const [selVersionId, setSelVersionId] = useState('');

  const [snapItemId, setSnapItemId] = useState('');
  const [docItemFilter, setDocItemFilter] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [genTypeId, setGenTypeId] = useState(''); // === WT3 docs ===: тип для /generation-job/start/
  const [genSnapshotId, setGenSnapshotId] = useState('');
  const [jobId, setJobId] = useState('');
  const [genErr, setGenErr] = useState<unknown>(null);
  const [genNotice, setGenNotice] = useState('');
  const [genBusy, setGenBusy] = useState(false);

  const [checkSnapshotId, setCheckSnapshotId] = useState('');
  const [approvalContactId, setApprovalContactId] = useState('');
  const [approvalId, setApprovalId] = useState('');
  const [decision, setDecision] = useState<'approve' | 'reject'>('approve');
  const [evidence, setEvidence] = useState(''); // 'msg:<id>' | 'file:<id>' | ''
  const [regType, setRegType] = useState('');
  const [regNumber, setRegNumber] = useState('');
  const [regIssuedDate, setRegIssuedDate] = useState('');
  const [regExpiresAt, setRegExpiresAt] = useState('');
  const [compareId, setCompareId] = useState('');
  const [compareResult, setCompareResult] = useState<VersionCompareResult | null>(null);

  const [cmdErr, setCmdErr] = useState<unknown>(null);
  const [cmdNotice, setCmdNotice] = useState('');
  const [cmdBusy, setCmdBusy] = useState(false);
  const [resolveDecision, setResolveDecision] = useState<'accept' | 'reject'>('accept');
  const [resolveReason, setResolveReason] = useState('');
  const [approvedVersionId, setApprovedVersionId] = useState('');
  const [finalFile, setFinalFile] = useState<File | null>(null);
  const [replaceReason, setReplaceReason] = useState('');

  // === WT3 docs ===: проверка реестра по выбранному IssuedDocument (§9).
  const [rcIssuedId, setRcIssuedId] = useState('');
  const [rcResult, setRcResult] = useState('');
  const [rcLink, setRcLink] = useState('');
  const [rcComment, setRcComment] = useState('');

  const job = useAsync<GenerationJob | null>(
    () => (jobId ? documents.generationJob(jobId).catch(() => null) : Promise.resolve(null)),
    [jobId],
  );
  const jobStatus = job.data?.status ?? '';
  /** Недостающие поля шаблона из 400 generation-job (label либо field path). */
  const genMissing = missingFieldsOf(genErr);

  // Поллинг пока job в работе.
  useEffect(() => {
    if (!jobId || (jobStatus !== 'pending' && jobStatus !== 'running' && jobStatus !== '')) return;
    const t = window.setInterval(() => job.reload(), 2500);
    return () => window.clearInterval(t);
  }, [jobId, jobStatus, job.reload]);

  const selDoc = selDocId ? docMap.get(selDocId) : undefined;
  const selVersion = selVersionId ? versionMap.get(selVersionId) : undefined;
  const docVersions = useMemo(
    () => (versions.data ?? [])
      .filter((v) => !selDocId || v.document === selDocId)
      .sort((a, b) => (b.version ?? 0) - (a.version ?? 0)),
    [versions.data, selDocId],
  );
  /** Версии выбранного документа с согласованием «approved» — кандидаты макета для upload-final. */
  const approvedVersions = useMemo(
    () => docVersions.filter((v) => (approvalsByVersion.get(v.id) ?? []).some((a) => a.status === 'approved')),
    [docVersions, approvalsByVersion],
  );
  /** По выбранному согласованному макету уже есть полученная финальная версия —
      повторная загрузка требует причины замены (F3-01: 400 replace_reason_required). */
  const hasReceivedChild = useMemo(
    () => Boolean(approvedVersionId) && (versions.data ?? []).some(
      (v) => v.origin === 'received' && v.parent_version === approvedVersionId),
    [approvedVersionId, versions.data],
  );
  const selChecks = selVersionId ? (checksByVersion.get(selVersionId) ?? []) : [];
  // === WT3 docs ===: гейт регистрации — последняя проверка вида 'reference'
  // (или legacy без kind); final_fields — отдельная история (§9).
  const lastCheck = selChecks.find((c) => c.kind !== 'final_fields') ?? selChecks[0] ?? null;
  const selApprovals = selVersionId ? (approvalsByVersion.get(selVersionId) ?? []) : [];
  const pendingApprovals = selApprovals.filter((a) => a.status === 'pending');
  const selIssued = selVersionId ? (issuedByVersion.get(selVersionId) ?? []) : [];
  const lastCheckDetail = checkDetails(lastCheck?.discrepancies);

  // === WT3 docs ===: отдельная история final_fields-проверок (§9).
  const lastFinalCheck = useMemo(
    () => selChecks.find((c) => c.kind === 'final_fields') ?? null,
    [selChecks],
  );
  const lastFinalDetail = checkDetails(lastFinalCheck?.discrepancies);

  // === WT3 docs ===: история проверок реестра выбранного выданного документа.
  const regHistory = useAsync<RegistryCheck[]>(
    () => (rcIssuedId ? documents.registryChecks(rcIssuedId).catch(() => [] as RegistryCheck[]) : Promise.resolve([] as RegistryCheck[])),
    [rcIssuedId],
  );

  /** Снимок, с которым сверялась последняя проверка (либо снимок самой
      версии) — источник имён продуктов для подписей расхождений (F2-04). */
  const checkSnapshot = useMemo(() => {
    const sid = lastCheck?.snapshot ?? selVersion?.snapshot ?? '';
    return sid ? (snapshots.data ?? []).find((s) => s.id === sid) : undefined;
  }, [lastCheck?.snapshot, selVersion?.snapshot, snapshots.data]);

  /** Имя продукта снимка по индексам items.<i>.products.<j> — подпись
      «продукт 2 «Насос…»» в строках расхождений. '' — если недоступно. */
  const productNameAt = (itemIdx: number, prodIdx: number): string => {
    const items = (checkSnapshot?.structured_data as { items?: unknown } | undefined)?.items;
    if (!Array.isArray(items) || prodIdx < 0) return '';
    const item = items[itemIdx < 0 ? 0 : itemIdx] as { products?: unknown } | undefined;
    const products = item?.products;
    const p = Array.isArray(products) ? products[prodIdx] as { name?: unknown } | undefined : undefined;
    return typeof p?.name === 'string' && p.name.trim() ? p.name.trim() : '';
  };

  /** Фильтр списка документов по позиции (двухпозиционная заявка, S4-07). */
  const filteredDocs = useMemo(() => {
    const list = docs.data ?? [];
    if (!docItemFilter) return list;
    if (docItemFilter === '__request__') return list.filter((d) => !d.request_item);
    return list.filter((d) => d.request_item === docItemFilter);
  }, [docs.data, docItemFilter]);

  const selectVersion = (v: DocumentVersion) => {
    setSelVersionId(v.id);
    setSelDocId(v.document ?? '');
    setCompareResult(null);
    setCmdErr(null);
    setCmdNotice('');
    setEvidence('');
    setApprovalId('');
    setApprovedVersionId('');
    setFinalFile(null);
    setReplaceReason('');
    setRegType(selDoc?.type ?? docMap.get(v.document ?? '')?.type ?? '');
  };

  const reloadDetail = () => {
    allChecks.reload();
    allApprovals.reload();
    allIssued.reload();
    versions.reload();
    docs.reload();
    blobs.reload();
  };

  // ---------- Команды ----------

  const takeSnapshot = async () => {
    setGenBusy(true); setGenErr(null); setGenNotice('');
    try {
      const res = await requests.takeSnapshot(requestId, data.request.revision, snapItemId || undefined);
      const scope = snapItemId ? (itemMap.get(snapItemId)?.title ?? 'выбранная позиция') : 'вся заявка';
      setGenNotice(`Снимок создан: v${res?.version ?? '?'} (${shortId(res?.snapshot_id ?? res?.id)}), ${scope}.`);
      snapshots.reload();
      data.reload();
    } catch (x) { setGenErr(x); } finally { setGenBusy(false); }
  };

  const generate = async () => {
    if ((!templateId && !genTypeId) || !genSnapshotId) return;
    setGenBusy(true); setGenErr(null); setGenNotice('');
    try {
      // === WT3 docs ===: если выбран тип без явного шаблона — сервер берёт
      // последний активный применимый шаблон (generation-job/start, §10).
      // Терминальный 'template_unapproved' приходит 201/200 — показываем как есть.
      const res = templateId
        ? await documents.createGenerationJob({ template_id: templateId, snapshot_id: genSnapshotId })
        : await documents.startGenerationByType({ snapshot_id: genSnapshotId, type: genTypeId });
      const id = res?.job_id ?? res?.id ?? '';
      setJobId(id);
      setGenNotice(`Задача генерации создана (${shortId(id)}), статус: ${JOB_LABELS[res?.status ?? ''] ?? res?.status ?? 'pending'}.`);
      jobs.reload();
    } catch (x) { setGenErr(x); } finally { setGenBusy(false); }
  };

  // Когда job завершился — подтягиваем версии (мог появиться result_version).
  useEffect(() => {
    if (jobStatus === 'done') { versions.reload(); docs.reload(); blobs.reload(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatus]);

  const runCheck = async () => {
    if (!selVersionId) return;
    setCmdBusy(true); setCmdErr(null); setCmdNotice('');
    try {
      const res = await documents.runCheck(selVersionId, checkSnapshotId || undefined);
      setCmdNotice(`Проверка выполнена: ${CHECK_LABELS[res?.result ?? ''] ?? res?.result ?? '—'}.`);
      allChecks.reload();
    } catch (x) { setCmdErr(x); } finally { setCmdBusy(false); }
  };

  // === WT3 docs ===: проверка подтверждённых полей финального документа
  // (origin='received') против снимка макета — DocumentCheck kind='final_fields' (§9).
  const runFinalCheck = async () => {
    if (!selVersionId) return;
    setCmdBusy(true); setCmdErr(null); setCmdNotice('');
    try {
      const res = await documents.finalCheck(selVersionId);
      setCmdNotice(`Проверка полей финального документа: ${CHECK_LABELS[res?.result ?? ''] ?? res?.result ?? '—'}.`);
      allChecks.reload();
    } catch (x) { setCmdErr(x); } finally { setCmdBusy(false); }
  };

  // === WT3 docs ===: фиксация проверки реестра выданного документа (§9).
  const createRegistryCheck = async () => {
    if (!rcIssuedId || !rcResult) return;
    setCmdBusy(true); setCmdErr(null); setCmdNotice('');
    try {
      await documents.registryCheck(rcIssuedId, {
        result: rcResult,
        link: rcLink.trim() || undefined,
        comment: rcComment.trim() || undefined,
      });
      setCmdNotice('Проверка реестра зафиксирована.');
      setRcResult(''); setRcLink(''); setRcComment('');
      regHistory.reload();
    } catch (x) { setCmdErr(x); } finally { setCmdBusy(false); }
  };

  const resolveCheckCmd = async () => {
    if (!selVersionId || !resolveReason.trim()) return;
    setCmdBusy(true); setCmdErr(null); setCmdNotice('');
    try {
      const res = await documents.resolveCheck(selVersionId, {
        decision: resolveDecision, reason: resolveReason.trim(),
      });
      setCmdNotice((res?.result === 'accepted_mismatch')
        ? 'Расхождения приняты проверяющим — версия допущена к регистрации.'
        : 'Расхождения отклонены — версия остаётся в статусе «есть расхождения».');
      setResolveReason('');
      allChecks.reload();
    } catch (x) { setCmdErr(x); } finally { setCmdBusy(false); }
  };

  const requestApproval = async () => {
    if (!selVersionId || !approvalContactId) return;
    setCmdBusy(true); setCmdErr(null); setCmdNotice('');
    try {
      await documents.requestApproval(selVersionId, approvalContactId);
      setCmdNotice('Запрос на согласование отправлен.');
      allApprovals.reload();
    } catch (x) { setCmdErr(x); } finally { setCmdBusy(false); }
  };

  const confirmApproval = async () => {
    if (!approvalId) return;
    const appr = selApprovals.find((a) => a.id === approvalId);
    setCmdBusy(true); setCmdErr(null); setCmdNotice('');
    try {
      const body: { decision: 'approve' | 'reject'; evidence_message_id?: string; evidence_file_id?: string; revision?: number } = {
        decision,
        revision: appr?.revision,
      };
      if (evidence.startsWith('msg:')) body.evidence_message_id = evidence.slice(4);
      if (evidence.startsWith('file:')) body.evidence_file_id = evidence.slice(5);
      await documents.confirmApproval(approvalId, body);
      setCmdNotice(decision === 'approve' ? 'Версия согласована.' : 'Версия отклонена.');
      allApprovals.reload();
    } catch (x) { setCmdErr(x); } finally { setCmdBusy(false); }
  };

  const registerIssued = async () => {
    const itemId = selDoc?.request_item;
    if (!itemId || !selVersionId || !regType || !regNumber) return;
    setCmdBusy(true); setCmdErr(null); setCmdNotice('');
    try {
      const body: { version_id: string; type: string; raw_number: string; issued_date?: string; expires_at?: string } = {
        version_id: selVersionId,
        type: regType,
        raw_number: regNumber,
      };
      if (regIssuedDate) body.issued_date = regIssuedDate;
      if (regExpiresAt) body.expires_at = regExpiresAt;
      await documents.registerIssued(itemId, body);
      setCmdNotice('Финальный документ зарегистрирован.');
      allIssued.reload();
    } catch (x) { setCmdErr(x); } finally { setCmdBusy(false); }
  };

  const compare = async () => {
    if (!selVersionId || !compareId) return;
    setCmdBusy(true); setCmdErr(null); setCmdNotice(''); setCompareResult(null);
    try {
      const res = await documents.compareVersions(selVersionId, compareId);
      setCompareResult(res);
    } catch (x) { setCmdErr(x); } finally { setCmdBusy(false); }
  };

  /** Загрузка финального «полученного» файла версией документа (upload-final, origin='received').
      При повторной загрузке по макету с received-потомком причина замены обязательна. */
  const uploadFinal = async () => {
    if (!selDocId || !approvedVersionId || !finalFile) return;
    if (hasReceivedChild && !replaceReason.trim()) {
      setCmdErr(new Error('Причина замены обязательна: по выбранному макету уже загружался финальный файл. Укажите, почему загружается новая версия.'));
      return;
    }
    setCmdBusy(true); setCmdErr(null); setCmdNotice('');
    try {
      const res = await documents.uploadFinal(selDocId, finalFile, {
        approved_version_id: approvedVersionId,
        revision: selDoc?.revision,
        reason: replaceReason.trim() || undefined,
      });
      setCmdNotice(`Финальный файл загружен: версия v${res?.version ?? '?'}, статус сканирования: ${res?.scan_status ?? '—'}.`);
      setFinalFile(null);
      setReplaceReason('');
      versions.reload();
      allChecks.reload();
      blobs.reload();
      docs.reload();
    } catch (x) {
      if (x instanceof ApiError && x.code === 'replace_reason_required') {
        setCmdErr(new Error('Причина замены обязательна: по выбранному согласованному макету уже есть загруженный финальный файл. Укажите, почему загружается новая версия.'));
      } else {
        setCmdErr(x);
      }
    } finally { setCmdBusy(false); }
  };

  // ---------- Таблицы ----------

  const docCols: Column<Document>[] = [
    { key: 'type', title: 'Тип', render: (r) => label(r.type) },
    { key: 'request_item', title: 'Позиция', render: (r) => (r.request_item ? itemMap.get(r.request_item)?.title ?? shortId(r.request_item) : 'Вся заявка') },
    { key: 'current_version', title: 'Текущая версия', render: (r) => (r.current_version ? `v${versionMap.get(r.current_version)?.version ?? '?'} (${shortId(r.current_version)})` : '—') },
    { key: 'visibility', title: 'Видимость', render: (r) => label(r.visibility) },
    { key: 'created_at', title: 'Создан', render: (r) => fmtDate(r.created_at) },
  ];

  const versionCols: Column<DocumentVersion>[] = [
    { key: 'document', title: 'Документ', render: (r) => docLabel(docMap.get(r.document ?? '')) },
    { key: 'version', title: 'Версия', render: (r) => `v${r.version ?? '?'}` },
    { key: 'origin', title: 'Источник', render: (r) => (
        <>
          <Badge value={r.origin} labels={ORIGIN_LABELS} tones={ORIGIN_TONES} />
          {r.origin === 'received' && r.replace_reason ? (
            <div className="muted" title={`Причина замены: ${r.replace_reason}`}>
              Причина: {r.replace_reason}
            </div>
          ) : null}
        </>
      ) },
    { key: 'scan', title: 'Скан', render: (r) => (
        <Badge value={r.file_blob ? blobMap.get(r.file_blob)?.scan_status : undefined}
               labels={SCAN_LABELS} tones={SCAN_TONES} />
      ) },
    { key: 'snapshot', title: 'Снимок', render: (r) => (r.snapshot ? shortId(r.snapshot) : '—') },
    { key: 'check', title: 'Проверка', render: (r) => {
        // === WT3 docs ===: гейт регистрации — проверка вида 'reference';
        // final_fields-проверки показываются в отдельной секции (§9).
        const ref = (checksByVersion.get(r.id) ?? []).find((c) => c.kind !== 'final_fields');
        return <Badge value={ref?.result} labels={CHECK_LABELS} tones={CHECK_TONES} />;
      } },
    { key: 'approval', title: 'Согласование', render: (r) => <Badge value={approvalsByVersion.get(r.id)?.[0]?.status} labels={APPROVAL_LABELS} tones={APPROVAL_TONES} /> },
    { key: 'file_blob', title: 'Файл', render: (r) => {
        const b = r.file_blob ? blobMap.get(r.file_blob) : undefined;
        const fname = b?.filename ?? b?.original_name ?? b?.name ?? '';
        return r.file_blob
          ? <a className="btn small" href={documents.downloadUrl(r.file_blob)} download={fname} target="_blank" rel="noreferrer">Скачать</a>
          : '—';
      } },
    { key: 'created_at', title: 'Создана', render: (r) => fmtDate(r.created_at) },
  ];

  const jobCols: Column<GenerationJob>[] = [
    { key: 'created_at', title: 'Создана', render: (r) => fmtDate(r.created_at) },
    { key: 'template', title: 'Шаблон', render: (r) => {
        const t = r.template ? templateMap.get(r.template) : undefined;
        return t ? templateLabel(t) : shortId(r.template);
      } },
    { key: 'snapshot', title: 'Снимок', render: (r) => shortId(r.snapshot) },
    { key: 'status', title: 'Статус', render: (r) => <Badge value={r.status} labels={JOB_LABELS} tones={JOB_TONES} /> },
    { key: 'result_version', title: 'Результат', render: (r) => (r.result_version ? versionLabel(versionMap.get(r.result_version)) : (r.last_error ?? '—')) },
  ];

  const approvalCols: Column<DocumentApproval>[] = [
    { key: 'contact', title: 'Контакт', render: (r) => contactLabel(r.contact, r.contact_name) },
    { key: 'status', title: 'Статус', render: (r) => <Badge value={r.status} labels={APPROVAL_LABELS} tones={APPROVAL_TONES} /> },
    {
      key: 'evidence', title: 'Подтверждение', render: (r) => (
        r.evidence_file
          ? <a className="btn small" href={documents.downloadUrl(r.evidence_file)} target="_blank" rel="noreferrer">Файл</a>
          : r.evidence_message ? `Сообщение ${shortId(r.evidence_message)}` : '—'
      ),
    },
    { key: 'confirmed_at', title: 'Решение', render: (r) => fmtDate(r.confirmed_at) },
    { key: 'created_at', title: 'Запрошено', render: (r) => fmtDate(r.created_at) },
  ];

  const issuedCols: Column<IssuedDocument>[] = [
    { key: 'type', title: 'Тип', render: (r) => label(r.type) },
    { key: 'raw_number', title: 'Номер', render: (r) => r.raw_number || '—' },
    { key: 'normalized_number', title: 'Норм. номер', render: (r) => r.normalized_number || '—' },
    { key: 'issued_date', title: 'Выдан', render: (r) => fmtDate(r.issued_date) },
    { key: 'expires_at', title: 'Действует до', render: (r) => fmtDate(r.expires_at) },
    // === WT3 docs ===: поставщик скрыт по правам — показываем маску.
    { key: 'supplier', title: 'Поставщик', render: (r) => (
        r.supplier_masked ? <span className="muted">скрыт по правам</span>
                          : (r.supplier ? shortId(r.supplier) : '—')
      ) },
    // === WT3 docs ===: переход к фиксации проверки реестра этого документа (§9).
    { key: 'registry', title: 'Реестр', render: (r) => (
        <button className="btn small" onClick={(e) => { e.stopPropagation(); setRcIssuedId(r.id ?? ''); }}>
          Проверка реестра
        </button>
      ) },
  ];

  const rcIssued = rcIssuedId ? (selIssued.find((i) => i.id === rcIssuedId)
    ?? (allIssued.data ?? []).find((i) => i.id === rcIssuedId)) : undefined;

  const registryCols: Column<RegistryCheck>[] = [
    { key: 'checked_at', title: 'Проверено', render: (r) => fmtDate(r.checked_at ?? r.created_at) },
    { key: 'result', title: 'Результат', render: (r) => (
        <Badge value={r.result} labels={REGISTRY_LABELS} tones={REGISTRY_TONES} />
      ) },
    { key: 'link', title: 'Ссылка', render: (r) => (r.link
        ? <a href={r.link} target="_blank" rel="noreferrer">{r.link}</a> : '—') },
    { key: 'comment', title: 'Комментарий', render: (r) => r.comment || '—' },
    { key: 'checked_by', title: 'Кто', render: (r) => (r.checked_by ? String(r.checked_by) : '—') },
  ];

  const regTypeOptions = useMemo(() => {
    const base = new Set(ISSUED_DOC_TYPES);
    if (selDoc?.type) base.add(selDoc.type);
    return [...base];
  }, [selDoc?.type]);

  // === WT3 docs ===: типы для генерации без явного шаблона — union
  // заявление/ДУ/доверенность (§10) и типы активных шаблонов из справочника.
  const genTypeOptions = useMemo(() => {
    const base = new Set<string>(['application', 'du', 'poa']);
    for (const t of templates.data ?? []) {
      if (t.type) base.add(t.type);
    }
    return [...base];
  }, [templates.data]);

  return (
    <div>
      <Card title="Генерация макета"
            actions={<button className="btn" onClick={() => { snapshots.reload(); templates.reload(); jobs.reload(); }}>Обновить</button>}>
        <ErrorBox error={genErr} />
        {genMissing.length > 0 && (
          <div className="alert alert-error" data-testid="generation-missing-fields">
            <strong>Не хватает данных снимка — генерация документа невозможна:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
              {genMissing.map((m, i) => (
                <li key={i}>
                  {m.label ?? humanFieldPath(m.field)}
                  {m.reason ? ` — ${REASON_LABELS[m.reason] ?? m.reason}` : ''}
                  {m.label && m.field ? <span className="muted"> ({m.field})</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
        {genNotice && <div className="alert alert-info">{genNotice}</div>}
        <div className="inline-form">
          <div className="form-row">
            {multiItem && (
              <Field label="Позиция для снимка"
                     hint="Снимок позиции привяжет сгенерированный документ к ней">
                <select value={snapItemId} onChange={(e) => setSnapItemId(e.target.value)}>
                  <option value="">Вся заявка</option>
                  {data.items.map((it) => <option key={it.id} value={it.id}>{it.title}</option>)}
                </select>
              </Field>
            )}
            <Field label="Шаблон документа">
              <select value={templateId} onChange={(e) => { setTemplateId(e.target.value); if (e.target.value) setGenTypeId(''); }}>
                <option value="">— выберите шаблон —</option>
                {(templates.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>{templateLabel(t)}</option>
                ))}
              </select>
            </Field>
            {/* === WT3 docs ===: генерация по типу — шаблон выбирает сервер
                (последний активный применимый); несогласованный образец →
                job 'template_unapproved' без очереди. */}
            {!templateId && (
              <Field label="…или тип документа"
                     hint="Сервер подберёт последний активный шаблон этого типа">
                <select value={genTypeId} onChange={(e) => setGenTypeId(e.target.value)}>
                  <option value="">— выберите тип —</option>
                  {genTypeOptions.map((t) => <option key={t} value={t}>{label(t)}</option>)}
                </select>
              </Field>
            )}
            <Field label="Снимок данных заявки">
              <select value={genSnapshotId} onChange={(e) => setGenSnapshotId(e.target.value)}>
                <option value="">— выберите снимок —</option>
                {snapshotList.map((s) => (
                  <option key={s.id} value={s.id}>v{s.version ?? '?'} · {snapshotScopeLabel(s)} · {fmtDate(s.confirmed_at ?? s.created_at)}</option>
                ))}
              </select>
            </Field>
            <button className="btn" onClick={takeSnapshot} disabled={genBusy}>
              Снять снимок
            </button>
            <button className="btn primary" onClick={generate}
                    disabled={genBusy || (!templateId && !genTypeId) || !genSnapshotId}>
              {genBusy ? 'Выполняется…' : 'Сгенерировать макет'}
            </button>
          </div>
          <div className="field-hint">
            Снимок фиксирует подтверждённые данные заявки (POST /requests/request/{'{id}'}/take-snapshot/),
            генерация рендерит шаблон по снимку (POST /documents/generation-job/).
            {multiItem && ' Без выбора позиции снимок охватывает всю заявку — такой документ нельзя зарегистрировать на позицию.'}
            {' '}Если режим выпуска позиции (вкладка «Позиции и варианты») менялся после снимка — требуется новый снимок.
          </div>
        </div>
        {jobId && (
          <div className="kv-grid" style={{ marginBottom: 8 }}>
            <KV k="Задача генерации" v={<span className="muted">{jobId}</span>} />
            <KV k="Статус" v={<Badge value={jobStatus} labels={JOB_LABELS} tones={JOB_TONES} />} />
            {job.data?.result_version && <KV k="Результат" v={versionLabel(versionMap.get(job.data.result_version))} />}
            {job.data?.last_error && <KV k="Ошибка" v={job.data.last_error} />}
          </div>
        )}
        <DataTable columns={jobCols} rows={requestJobs} empty="Задач генерации нет" />
      </Card>

      <Card title="Документы заявки"
            actions={<button className="btn" onClick={reloadDetail}>Обновить</button>}>
        <ErrorBox error={docs.error} />
        {multiItem && (
          <div className="form-row" style={{ marginBottom: 8 }}>
            <Field label="Фильтр по позиции">
              <select value={docItemFilter} onChange={(e) => setDocItemFilter(e.target.value)}>
                <option value="">Все</option>
                {data.items.map((it) => <option key={it.id} value={it.id}>{it.title}</option>)}
                <option value="__request__">Вся заявка (без позиции)</option>
              </select>
            </Field>
          </div>
        )}
        <DataTable columns={docCols} rows={filteredDocs}
                   empty="Документов нет — загрузите файл на вкладке «Файлы» или сгенерируйте макет"
                   onRowClick={(r) => { setSelDocId(r.id); setSelVersionId(''); setCompareResult(null); }} />
      </Card>

      {selDocId && (
        <Card title={`Версии: ${docLabel(selDoc)}`}>
          <ErrorBox error={versions.error} />
          {docVersions.length === 0
            ? <Empty text="Версий нет" />
            : <DataTable columns={versionCols} rows={docVersions} onRowClick={selectVersion} />}
        </Card>
      )}

      {selVersion && (
        <Card title={`Версия ${versionLabel(selVersion)}`}>
          <div className="kv-grid" style={{ marginBottom: 8 }}>
            <KV k="ID версии" v={<span className="muted">{selVersion.id}</span>} />
            <KV k="Снимок" v={selVersion.snapshot ? shortId(selVersion.snapshot) : '—'} />
            <KV k="Создана" v={fmtDate(selVersion.created_at)} />
            <KV k="Файл" v={selVersion.file_blob
              ? (() => {
                  const b = blobMap.get(selVersion.file_blob);
                  const fname = b?.filename ?? b?.original_name ?? b?.name ?? '';
                  return <a className="btn small" href={documents.downloadUrl(selVersion.file_blob)} download={fname} target="_blank" rel="noreferrer">Скачать{fname ? ` (${fname})` : ''}</a>;
                })()
              : '—'} />
          </div>

          <ErrorBox error={cmdErr} />
          {cmdNotice && <div className="alert alert-info">{cmdNotice}</div>}

          <div className="section">
            <h3>Проверка против снимка</h3>
            <div className="kv-grid" style={{ marginBottom: 8 }}>
              <KV k="Результат" v={<Badge value={lastCheck?.result} labels={CHECK_LABELS} tones={CHECK_TONES} />} />
              <KV k="Проверено" v={fmtDate(lastCheck?.reviewed_at ?? lastCheck?.created_at)} />
            </div>
            {lastCheckDetail.resolution && (
              <div className="alert alert-info" style={{ marginBottom: 8 }}>
                Решение проверяющего: {lastCheckDetail.resolution.decision === 'accept'
                  ? 'расхождения приняты' : 'расхождения отклонены'}
                {lastCheckDetail.resolution.reason ? ` — ${lastCheckDetail.resolution.reason}` : ''}
                {lastCheckDetail.resolution.at ? ` (${fmtDate(lastCheckDetail.resolution.at)})` : ''}
              </div>
            )}
            {lastCheckDetail.list.length > 0 && (
              // F2-04: сырые field_path/JSON в UI не показываем — путь
              // гуманизируется («Позиция 1 · продукт 2 «имя» · модель»),
              // причина — русской подписью из REASON_LABELS.
              <DataTable<DocumentCheckDiscrepancy>
                columns={[
                  { key: 'field_path', title: 'Поле',
                    render: (r) => capFirst(humanFieldPath(r.field_path || r.field || '', productNameAt)) },
                  { key: 'role', title: 'Роль', render: (r) => (r.role ? label(r.role) : '—') },
                  { key: 'expected', title: 'Ожидалось', render: (r) => fmtExpected(r.expected ?? r.actual) },
                  { key: 'observed', title: 'Найдено в документе', render: (r) => fmtExpected(r.observed) },
                  { key: 'reason', title: 'Причина', render: (r) => (r.reason ? REASON_LABELS[r.reason] ?? label(r.reason) : '—') },
                ]}
                rows={lastCheckDetail.list}
              />
            )}
            {lastCheck?.result === 'mismatch' && (
              <div className="inline-form" style={{ marginTop: 8 }}>
                <div className="form-row">
                  <Field label="Решение по расхождениям"
                         hint="«Принять» переводит проверку в accepted_mismatch — допуск к регистрации">
                    <select value={resolveDecision}
                            onChange={(e) => setResolveDecision(e.target.value as 'accept' | 'reject')}>
                      <option value="accept">Принять расхождения</option>
                      <option value="reject">Отклонить</option>
                    </select>
                  </Field>
                  <Field label="Причина решения" hint="Обязательно — фиксируется в журнале проверки">
                    <input value={resolveReason} onChange={(e) => setResolveReason(e.target.value)}
                           placeholder="Например: расхождение некритично, подтверждено клиентом" />
                  </Field>
                  <button className="btn" onClick={resolveCheckCmd}
                          disabled={cmdBusy || !resolveReason.trim()}>
                    Принять/отклонить расхождения
                  </button>
                </div>
              </div>
            )}
            <div className="form-row" style={{ alignItems: 'flex-end', marginTop: 8 }}>
              <Field label="Снимок для проверки" hint="По умолчанию — снимок версии">
                <select value={checkSnapshotId} onChange={(e) => setCheckSnapshotId(e.target.value)}>
                  <option value="">— по умолчанию —</option>
                  {snapshotList.map((s) => (
                    <option key={s.id} value={s.id}>v{s.version ?? '?'} · {snapshotScopeLabel(s)} · {fmtDate(s.confirmed_at ?? s.created_at)}</option>
                  ))}
                </select>
              </Field>
              <button className="btn" onClick={runCheck} disabled={cmdBusy}>Проверить</button>
            </div>
          </div>

          {/* === WT3 docs ===: §9 — посполевая проверка финального документа.
              Сравниваются только подтверждённые извлечённые значения против
              снимка согласованного макета; результат — DocumentCheck
              kind='final_fields', в гейт регистрации не входит. */}
          {selVersion.origin === 'received' && (
            <div className="section" data-testid="final-fields-check">
              <h3>Проверка полей финального документа</h3>
              <div className="kv-grid" style={{ marginBottom: 8 }}>
                <KV k="Результат" v={<Badge value={lastFinalCheck?.result} labels={CHECK_LABELS} tones={CHECK_TONES} />} />
                <KV k="Проверено" v={fmtDate(lastFinalCheck?.reviewed_at ?? lastFinalCheck?.created_at)} />
              </div>
              {lastFinalDetail.list.length > 0 && (
                <DataTable<DocumentCheckDiscrepancy>
                  columns={[
                    { key: 'field_path', title: 'Поле',
                      render: (r) => capFirst(humanFieldPath(r.field_path || r.field || '', productNameAt)) },
                    { key: 'role', title: 'Роль', render: (r) => (r.role ? label(r.role) : '—') },
                    { key: 'status', title: 'Статус', render: (r) => (
                        <Badge value={r.status} labels={FIELD_STATUS_LABELS} tones={FIELD_STATUS_TONES} />
                      ) },
                    { key: 'expected', title: 'Эталон', render: (r) => fmtExpected(r.expected ?? r.actual) },
                    { key: 'observed', title: 'Найдено в документе', render: (r) => fmtExpected(r.observed) },
                    { key: 'reason', title: 'Причина', render: (r) => (r.reason ? REASON_LABELS[r.reason] ?? label(r.reason) : '—') },
                  ]}
                  rows={lastFinalDetail.list}
                />
              )}
              <div className="form-row" style={{ alignItems: 'flex-end', marginTop: 8 }}>
                <button className="btn" onClick={runFinalCheck} disabled={cmdBusy}>
                  Проверить поля документа
                </button>
                <div className="field-hint">
                  Извлечение запускается автоматически (engine=final_rules). В сравнение
                  входят только подтверждённые поля — неподтверждённые помечаются
                  «не подтверждено» и на результат не влияют.
                </div>
              </div>
            </div>
          )}

          <div className="section">
            <h3>Согласование</h3>
            <DataTable columns={approvalCols} rows={selApprovals} empty="Согласований нет" />
            <div className="form-row" style={{ alignItems: 'flex-end', marginTop: 8 }}>
              <Field label="Контакт для согласования">
                <select value={approvalContactId} onChange={(e) => setApprovalContactId(e.target.value)}>
                  <option value="">— выберите контакт —</option>
                  {contactOptions.map((c) => (
                    <option key={String(c.id)} value={String(c.id)}>{c.name}{c.position ? ` · ${c.position}` : ''}</option>
                  ))}
                </select>
              </Field>
              <button className="btn" onClick={requestApproval} disabled={cmdBusy || !approvalContactId}>
                На согласование
              </button>
            </div>
            {pendingApprovals.length > 0 && (
              <div className="inline-form" style={{ marginTop: 12 }}>
                <div className="form-row">
                  <Field label="Заявка на согласование">
                    <select value={approvalId} onChange={(e) => setApprovalId(e.target.value)}>
                      <option value="">— выберите —</option>
                      {pendingApprovals.map((a) => (
                        <option key={a.id} value={a.id}>{contactLabel(a.contact, a.contact_name)} · {fmtDate(a.created_at)}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Решение">
                    <select value={decision} onChange={(e) => setDecision(e.target.value as 'approve' | 'reject')}>
                      <option value="approve">Согласовать</option>
                      <option value="reject">Отклонить</option>
                    </select>
                  </Field>
                  <Field label="Письменное подтверждение (evidence)"
                         hint="Для «Согласовать» обязательно ровно одно: сообщение или файл">
                    <select value={evidence} onChange={(e) => setEvidence(e.target.value)}>
                      <option value="">— не выбрано —</option>
                      {requestMessages.map((m) => (
                        <option key={`msg:${m.id}`} value={`msg:${m.id}`}>
                          Сообщение: {m.subject || m.sender || shortId(m.id)} · {fmtDate(m.sent_at ?? m.created_at)}
                        </option>
                      ))}
                      {(blobs.data ?? []).map((b) => (
                        <option key={`file:${b.id}`} value={`file:${b.id}`}>
                          Файл: {b.filename ?? b.original_name ?? b.name ?? shortId(b.id)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <button className="btn primary" onClick={confirmApproval}
                          disabled={cmdBusy || !approvalId || (decision === 'approve' && !evidence)}>
                    Подтвердить решение
                  </button>
                </div>
              </div>
            )}
          </div>

          {approvedVersions.length > 0 && (
            <div className="section">
              <h3>Загрузка финального файла</h3>
              <div className="inline-form">
                <div className="form-row">
                  <Field label="Согласованный макет"
                         hint="Версия документа с согласованием «approved» — обязательна">
                    <select value={approvedVersionId} onChange={(e) => setApprovedVersionId(e.target.value)}>
                      <option value="">— выберите версию —</option>
                      {approvedVersions.map((v) => (
                        <option key={v.id} value={v.id}>v{v.version ?? '?'} · {fmtDate(v.created_at)}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Финальный файл">
                    <input key={selVersionId} type="file"
                           onChange={(e) => setFinalFile(e.target.files?.[0] ?? null)} />
                  </Field>
                  <Field label={hasReceivedChild ? 'Причина замены (обязательно)' : 'Причина замены'}
                         hint={hasReceivedChild
                           ? 'По этому макету уже загружался финальный файл — причина сохраняется в истории версии'
                           : 'Для первой загрузки финального файла причина не требуется'}>
                    <input value={replaceReason} onChange={(e) => setReplaceReason(e.target.value)}
                           aria-required={hasReceivedChild}
                           placeholder="Например: исправлены реквизиты в прежнем файле" />
                  </Field>
                  <button className="btn primary" onClick={uploadFinal}
                          disabled={cmdBusy || !approvedVersionId || !finalFile || (hasReceivedChild && !replaceReason.trim())}>
                    Загрузить финальный файл
                  </button>
                </div>
                <div className="field-hint">
                  POST /documents/document/{'{id}'}/upload-final/ — файл становится новой версией
                  (origin=received), родитель — согласованный макет. Статус антивирусного
                  сканирования (scan_status) показывается в уведомлении после загрузки.
                  При повторной загрузке по тому же макету причина замены обязательна
                  (иначе 400 replace_reason_required).
                </div>
              </div>
            </div>
          )}

          <div className="section">
            <h3>Регистрация финального документа</h3>
            <DataTable columns={issuedCols} rows={selIssued} empty="Зарегистрированных документов нет" />

            {/* === WT3 docs ===: §9 — проверка выданного документа в реестре.
                Каждая проверка — новая запись в истории (перезаписи нет),
                действие аудируется на backend. */}
            {selIssued.length > 0 && (
              <div className="inline-form" style={{ marginTop: 12 }} data-testid="registry-check">
                <div className="form-row">
                  <Field label="Выданный документ">
                    <select value={rcIssuedId} onChange={(e) => setRcIssuedId(e.target.value)}>
                      <option value="">— выберите —</option>
                      {selIssued.map((i) => (
                        <option key={i.id} value={i.id}>{label(i.type)} · {i.raw_number || shortId(i.id)}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Результат проверки">
                    <select value={rcResult} onChange={(e) => setRcResult(e.target.value)}>
                      <option value="">— выберите —</option>
                      <option value="found">Найден в реестре</option>
                      <option value="not_found">Не найден в реестре</option>
                      <option value="mismatch">Данные расходятся</option>
                      <option value="unavailable">Реестр недоступен</option>
                    </select>
                  </Field>
                  <Field label="Ссылка">
                    <input value={rcLink} onChange={(e) => setRcLink(e.target.value)}
                           placeholder="Ссылка на запись в реестре" />
                  </Field>
                  <Field label="Комментарий">
                    <input value={rcComment} onChange={(e) => setRcComment(e.target.value)}
                           placeholder="Необязательно" />
                  </Field>
                  <button className="btn" onClick={createRegistryCheck}
                          disabled={cmdBusy || !rcIssuedId || !rcResult}>
                    Зафиксировать проверку
                  </button>
                </div>
                {rcIssued && (
                  <div style={{ marginTop: 8 }}>
                    <ErrorBox error={regHistory.error} />
                    <DataTable columns={registryCols} rows={regHistory.data ?? []}
                               empty="Проверок реестра ещё не было" />
                  </div>
                )}
              </div>
            )}
            {selDoc?.request_item && selVersion.origin === 'received' ? (
              <div className="inline-form" style={{ marginTop: 12 }}>
                <div className="form-row">
                  <Field label="Тип документа">
                    <select value={regType} onChange={(e) => setRegType(e.target.value)}>
                      <option value="">— выберите —</option>
                      {regTypeOptions.map((t) => <option key={t} value={t}>{label(t)}</option>)}
                    </select>
                  </Field>
                  <Field label="Номер документа">
                    <input value={regNumber} onChange={(e) => setRegNumber(e.target.value)} placeholder="Как в оригинале" />
                  </Field>
                  <Field label="Дата выдачи">
                    <input type="date" value={regIssuedDate} onChange={(e) => setRegIssuedDate(e.target.value)} />
                  </Field>
                  <Field label="Действует до">
                    <input type="datetime-local" value={regExpiresAt} onChange={(e) => setRegExpiresAt(e.target.value)} />
                  </Field>
                  <button className="btn primary" onClick={registerIssued}
                          disabled={cmdBusy || !regType || !regNumber}>
                    Зарегистрировать
                  </button>
                </div>
                <div className="field-hint">
                  Регистрация возможна при последней проверке «match» и согласовании «approved»
                  (гейты проверяет backend — 409 с кодом причины).
                </div>
              </div>
            ) : selDoc?.request_item ? (
              <div className="field-hint" style={{ marginTop: 8 }}>
                Финальный документ — загруженный полученный файл (origin=received).
                Загрузите его в блоке «Загрузка финального файла» — регистрируется только полученная версия.
              </div>
            ) : (
              <div className="field-hint" style={{ marginTop: 8 }}>
                {multiItem
                  ? 'Документ привязан ко всей заявке — регистрация выполняется для документов позиции. Выберите позицию при создании снимка — тогда сгенерированный документ привяжется к ней.'
                  : 'Документ не привязан к позиции — регистрация выполняется для документов позиции.'}
              </div>
            )}
          </div>

          <div className="section">
            <h3>Сравнение версий</h3>
            <div className="form-row" style={{ alignItems: 'flex-end' }}>
              <Field label="Сравнить с версией">
                <select value={compareId} onChange={(e) => setCompareId(e.target.value)}>
                  <option value="">— выберите версию —</option>
                  {(versions.data ?? [])
                    .filter((v) => v.id !== selVersionId)
                    .map((v) => <option key={v.id} value={v.id}>{versionLabel(v)} · {fmtDate(v.created_at)}</option>)}
                </select>
              </Field>
              <button className="btn" onClick={compare} disabled={cmdBusy || !compareId}>Сравнить</button>
            </div>
            {compareResult && (
              <div style={{ marginTop: 8 }}>
                <div className="kv-grid" style={{ marginBottom: 8 }}>
                  <KV k="Результат" v={<Badge value={compareResult.result} labels={CHECK_LABELS} tones={CHECK_TONES} />} />
                  <KV k="Строк" v={`${compareResult.meta?.lines_a ?? '—'} / ${compareResult.meta?.lines_b ?? '—'}`} />
                </div>
                {(compareResult.differences ?? []).length > 0 && (
                  <DataTable
                    columns={[
                      { key: 'field', title: 'Поле/строка', render: (r) => r.field ?? '—' },
                      { key: 'a', title: 'Эта версия', render: (r) => r.a ?? '—' },
                      { key: 'b', title: 'Другая версия', render: (r) => r.b ?? '—' },
                    ]}
                    rows={compareResult.differences ?? []}
                  />
                )}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
